<script setup>
import { nextTick, useTemplateRef } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'input', 'blur']);

const textareaEl = useTemplateRef('textareaEl');

const focusSelection = async (selectionStart, selectionEnd) => {
  await nextTick();
  const el = textareaEl.value;
  if (!el) return;
  el.focus();
  el.setSelectionRange(selectionStart, selectionEnd);
};

const updateValue = async (nextValue, selectionStart, selectionEnd) => {
  emit('update:modelValue', nextValue);
  emit('input', nextValue);
  await focusSelection(selectionStart, selectionEnd);
};

const withSelection = async handler => {
  const el = textareaEl.value;
  if (!el) return;

  const value = props.modelValue || '';
  const start = el.selectionStart ?? 0;
  const end = el.selectionEnd ?? 0;

  const result = handler({ value, start, end });
  if (!result) return;

  await updateValue(
    result.value,
    result.selectionStart ?? start,
    result.selectionEnd ?? end
  );
};

const wrapSelection = async (prefix, suffix = prefix, fallback = '') => {
  await withSelection(({ value, start, end }) => {
    const selected = value.slice(start, end);
    const text = selected || fallback;
    const nextValue =
      value.slice(0, start) + prefix + text + suffix + value.slice(end);
    const selectionStart = start + prefix.length;
    const selectionEnd = selectionStart + text.length;

    return { value: nextValue, selectionStart, selectionEnd };
  });
};

const insertLink = async () => {
  await withSelection(({ value, start, end }) => {
    const selected = value.slice(start, end) || 'texto';
    const url = 'https://';
    const insertion = `[${selected}](${url})`;
    const nextValue = value.slice(0, start) + insertion + value.slice(end);
    const urlStart = start + selected.length + 3;
    const urlEnd = urlStart + url.length;

    return {
      value: nextValue,
      selectionStart: urlStart,
      selectionEnd: urlEnd,
    };
  });
};

const transformSelectedLines = async formatter => {
  await withSelection(({ value, start, end }) => {
    const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const lineEndIndex = value.indexOf('\n', end);
    const lineEnd = lineEndIndex === -1 ? value.length : lineEndIndex;
    const block = value.slice(lineStart, lineEnd);
    const lines = block.split('\n');
    const nextBlock = formatter(lines);
    const nextValue =
      value.slice(0, lineStart) + nextBlock + value.slice(lineEnd);

    return {
      value: nextValue,
      selectionStart: lineStart,
      selectionEnd: lineStart + nextBlock.length,
    };
  });
};

const applyBulletList = async () => {
  await transformSelectedLines(lines =>
    lines.map(line => (line.trim() ? `- ${line}` : '- ')).join('\n')
  );
};

const applyOrderedList = async () => {
  await transformSelectedLines(lines =>
    lines
      .map((line, index) => `${index + 1}. ${line.trim() ? line : ''}`.trimEnd())
      .join('\n')
  );
};

const onInput = event => {
  const nextValue = event.target.value;
  emit('update:modelValue', nextValue);
  emit('input', nextValue);
};
</script>

<template>
  <div class="canned-markdown-editor">
    <div class="canned-markdown-toolbar">
      <button type="button" class="canned-markdown-tool" @click="wrapSelection('**', '**', 'texto')">
        B
      </button>
      <button type="button" class="canned-markdown-tool italic" @click="wrapSelection('*', '*', 'texto')">
        I
      </button>
      <button type="button" class="canned-markdown-tool" @click="wrapSelection('`', '`', 'codigo')">
        ``
      </button>
      <button type="button" class="canned-markdown-tool" @click="insertLink">
        Link
      </button>
      <button type="button" class="canned-markdown-tool" @click="applyBulletList">
        • Lista
      </button>
      <button type="button" class="canned-markdown-tool" @click="applyOrderedList">
        1. Lista
      </button>
      <button type="button" class="canned-markdown-tool" @click="wrapSelection('{{', '}}', 'variavel')">
        {{}}
      </button>
    </div>

    <textarea
      ref="textareaEl"
      class="canned-markdown-textarea"
      :value="modelValue"
      :placeholder="placeholder"
      spellcheck="false"
      @input="onInput"
      @blur="$emit('blur')"
    />
  </div>
</template>

<style scoped lang="scss">
.canned-markdown-editor {
  @apply rounded-xl border border-n-weak bg-n-alpha-black2 overflow-hidden;
}

.canned-markdown-toolbar {
  @apply flex flex-wrap items-center gap-2 px-3 py-3 border-b border-n-weak bg-n-alpha-2;
}

.canned-markdown-tool {
  @apply inline-flex items-center justify-center h-8 px-3 rounded-lg border border-n-strong bg-n-solid-3 text-n-slate-12 text-sm font-medium transition-colors;

  &:hover {
    @apply bg-n-alpha-2;
  }
}

.italic {
  font-style: italic;
}

.canned-markdown-textarea {
  @apply w-full min-h-[14rem] resize-y bg-transparent text-n-slate-12 px-3 py-3 outline-none border-0;
  line-height: 1.5;
}
</style>
